import { BotPartSlot, PlayerGrouping } from '@nova/types';

import { MatchOutcomeEnum } from '../consts';

import { DataTrackLabelEnum } from './data-track-label.enum';

export interface IAnalyticsEvent {
  eventType: DataTrackLabelEnum | string;
  userId?: string;
  classId?: string;
  data?: object;
}

export interface IAnalyticsUserEvent extends IAnalyticsEvent {
  userId: string;
}

export enum DisconnectReason {
  menuOption = 'menuOption',
  browser = 'browser',
  AFK = 'AFK',
  pagsExpired = 'pagsExpired'
}

export enum EngineType {
  GES = 'ges',
  LOCAL = 'local'
}

// Full Match Analytics - uploaded to Azure Blob Storage
// Contains all detailed match and player statistics for analysis
export interface IMatchFullAnalytics {
  matchId: string; // Unique identifier for the match
  matchMode: PlayerGrouping; // Intended mode of play; SP = Single Player (solo), MP = Multiplayer (PvP or Co-op)
  engineType: EngineType; // Engine running the match; GES = remote game engine service, LOCAL = client-side engine
  startDateTime: Date; // Start date and time of the match
  endDateTime: Date; // End date and time of the match
  durationSeconds: number; // Duration of the match in seconds
  mapId: string; // Name or identifier of the map played
  totalAIKilled: number; // Total amount of AI Ants killed in the match
  players: IMatchPlayerFullAnalytics[]; // Array of player analytics
  capturePoints: {
    timeNeutral: number; // How much time in seconds the capture point was neutral
    timeCapturedByLeftTeam: number; // How much time in seconds the capture point was owned by the left team
    timeCapturedByRightTeam: number; // How much time in seconds the capture point was owned by the right team
  }[];
}

// Match Analytics for REST server endpoint
// Contains minimal data with Azure blob URL reference to full analytics
export interface IMatchAnalytics {
  matchId: string; // Unique identifier for the match
  mapId: string; // Name or identifier of the map played
  startDateTime: Date; // Start date and time of the match
  endDateTime: Date; // End date and time of the match
  players: IMatchPlayerAnalytics[]; // Array of basic player info
}

export interface PartAnalytics {
  gear: string;
  swatch: string;
}

export type LoadoutAnalytics = Record<BotPartSlot, PartAnalytics>;

export interface IBoostAnalytics {
  boostId: string;
  selectBoostOnPurchase: boolean;
}

// Full player analytics for Azure Blob Storage
// Contains all detailed statistics for a player in a match
export interface IMatchPlayerFullAnalytics {
  playerId: string; // Unique identifier of the player
  teamID: number; // Team identifier of the player
  aiPlayer: boolean; // AI player
  matchOutcome: number; // -1: loss, 0: tie, 1: win
  kills: number; // Number of kills made by the player
  tutorialStep: string; // Tutorial step the player was on
  loadout: Record<BotPartSlot, PartAnalytics>; // Each zo-gear part equipped
  boost?: IBoostAnalytics;
  deaths: {
    deathNumber: number; // Sequential number of the death
    location: {
      x: number;
      y: number;
    };
    timeStamp: Date; // Match timestamp when the player died
    killingBlowEntity: string; // Entity that delivered the killing blow
    damageType: string; // Type of damage that caused death
  }[];
  combatStats: {
    timesInCombat: number; // Number of times the player was "in combat"
    totalCombatDurationSeconds: number; // Total time spent in combat
  };
  skillsUsed: {
    skillType: string; // Name of the skill used by the player
    usageCount: number; // Number of times the individual skill was used
  }[];
  healthPackUsage: {
    totalUsed: number; // Total number of health packs used
    usageDetails: {
      location: {
        x: number;
        y: number;
      };
      timeStamp: Date; // Match timestamp when the health pack was used
    }[];
  };
  disconnectionEvents: {
    reason: DisconnectReason; // Reason the player disconnected
    timeStamp: Date; // Timestamp when the player disconnected
    location: {
      x: number;
      y: number;
    };
  }[];
  deviceInfo: {
    platform: string;
    browser: string;
    version: string;
  };
}

// Player analytics for REST server endpoint
// Contains only essential player data
export interface IMatchPlayerAnalytics {
  playerId: string; // Unique identifier of the player
  teamID: number; // Team identifier of the player
  matchOutcome: MatchOutcomeEnum; // Match outcome for the player
  loadout: Record<BotPartSlot, PartAnalytics>; // Each zo-gear part equipped
}
